package com.qrkontaq.ui.tenant

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.qrkontaq.core.viewmodel.rememberViewModel
import com.qrkontaq.data.dto.MenuCategoryDto
import com.qrkontaq.data.dto.PublicMenuItemDto
import com.qrkontaq.data.dto.PublicRestaurantDto
import com.qrkontaq.data.dto.PublicTableCheckDto
import com.qrkontaq.data.dto.formatPrice
import com.qrkontaq.data.dto.localized
import com.qrkontaq.data.repository.PublicRepository
import com.qrkontaq.di.LocalAppContainer
import com.qrkontaq.tenant.MenuUiState
import com.qrkontaq.tenant.MenuViewModel
import com.qrkontaq.ui.theme.Brand
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.ObjectFit
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.MainScope
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.FlexWrap
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.vh
import org.jetbrains.compose.web.dom.A
import org.jetbrains.compose.web.dom.Input
import org.w3c.dom.events.Event

/** Entry point of the tenant public menu site. */
@Composable
fun MenuScreen(
    slug: String,
    tableLabel: String? = null,
    categorySlug: String? = null,
) {
    val container = LocalAppContainer.current
    val repository = remember { PublicRepository(container.defaultHttpClient) }
    val viewModel =
        rememberViewModel(key = slug) {
            MenuViewModel(repository, slug, tableLabel, categorySlug)
        }

    when (val state = viewModel.state) {
        is MenuUiState.Loading -> MenuLoadingView()
        is MenuUiState.NotFound -> MenuNotFoundView()
        is MenuUiState.Frozen -> FrozenView(state.restaurant)
        is MenuUiState.Ready -> MenuReadyView(state, tableLabel, categorySlug)
    }
}

@Composable
private fun MenuReadyView(
    state: MenuUiState.Ready,
    tableLabel: String?,
    initialCategorySlug: String?,
) {
    val restaurant = state.restaurant
    val accent = Brand.accentFrom(restaurant.accentColor)
    val accentHex = Brand.accentHexFrom(restaurant.accentColor)
    // Among the menu's available languages: the visitor's system language wins,
    // then English, otherwise the restaurant's configured default.
    var lang by remember {
        mutableStateOf(
            com.qrkontaq.utils.resolveLanguage(
                saved = null,
                supported = restaurant.languages,
                fallback = if ("en" in restaurant.languages) "en" else restaurant.defaultLanguage,
            ),
        )
    }
    val t = menuT(lang)
    var filters by remember { mutableStateOf(setOf<String>()) }
    var filtersOpen by remember { mutableStateOf(false) }
    var rateOpen by remember { mutableStateOf(false) }
    var selectedDish by remember { mutableStateOf<PublicMenuItemDto?>(null) }
    var infoOpen by remember { mutableStateOf(false) }

    // Like counts changed in this session (itemId -> fresh count). The menu DTO is
    // immutable state from the ViewModel, so list cards and showcases read their
    // like counters through this overlay and update instantly after a vote.
    var likeOverrides by remember { mutableStateOf(mapOf<String, Long>()) }
    fun PublicMenuItemDto.withLiveLikes(): PublicMenuItemDto =
        likeOverrides[id]?.let { copy(likes = it) } ?: this
    var loyaltyInfo by remember { mutableStateOf<GuestApi.LoyaltyInfo?>(null) }

    // Explicit Google/Apple session state driving the loyalty section in the
    // burger drawer. Loyalty never calls ensureSession(): the balance loads only
    // after an explicit sign-in; until then the section shows the blurred-QR
    // teaser. The issued code is hoisted here so reopening the drawer reuses a
    // still-valid code (codes are only issued while the drawer is open).
    var loyaltySignedIn by remember { mutableStateOf(GuestSession.isOAuth) }
    var loyaltyQr by remember { mutableStateOf<GuestApi.LoyaltyQr?>(null) }

    LaunchedEffect(restaurant.slug, restaurant.loyaltyEnabled) {
        if (restaurant.loyaltyEnabled && !GuestSession.isOAuth) {
            // Cookie SSO: a guest who signed in on any other venue subdomain is
            // silently logged in here too — no popup, no clicks.
            if (GuestApi.trySilentSession()) loyaltySignedIn = true
        }
        if (restaurant.loyaltyEnabled && GuestSession.isOAuth) {
            loyaltyInfo = GuestApi.loyalty(restaurant.slug)
            // A 403 LOYALTY_AUTH_REQUIRED (stale anon token) drops the OAuth flag.
            loyaltySignedIn = GuestSession.isOAuth
        }
    }

    // Rebrand the browser tab + shareable link preview for this venue: title →
    // name, favicon → logo, OG/Twitter banner → cover. Falls back to the baked-in
    // QRkontaq defaults for whatever the venue hasn't uploaded.
    LaunchedEffect(restaurant.slug, lang) {
        com.qrkontaq.utils.SeoHelper.applyTenantBranding(
            name = restaurant.name,
            description = restaurant.description.localized(lang),
            logoUrl = restaurant.logoUrl,
            coverUrl = restaurant.coverUrl,
        )
    }

    InjectMenuStyles(accentHex)

    val categorySlugs = remember(state.menu) { state.menu.categories.map { it.category.slug } }
    var activeCategory by remember(categorySlugs) { mutableStateOf(categorySlugs.firstOrNull()) }

    // Scroll-spy: highlight the category whose section is currently under the sticky bar.
    DisposableEffect(categorySlugs) {
        val listener: (Event) -> Unit = {
            var current = categorySlugs.firstOrNull()
            for (slug in categorySlugs) {
                val el = document.getElementById("cat-$slug")
                if (el != null && el.getBoundingClientRect().top <= 140.0) current = slug
            }
            if (current != activeCategory) activeCategory = current
        }
        window.addEventListener("scroll", listener)
        onDispose { window.removeEventListener("scroll", listener) }
    }

    LaunchedEffect(activeCategory) {
        activeCategory?.let { slug ->
            document.getElementById("chip-$slug")
                ?.scrollIntoView(js("{ behavior: 'smooth', inline: 'center', block: 'nearest' }"))
        }
    }

    LaunchedEffect(initialCategorySlug) {
        if (initialCategorySlug != null) {
            kotlinx.coroutines.delay(200)
            document.getElementById("cat-$initialCategorySlug")
                ?.scrollIntoView(js("{ behavior: 'smooth', block: 'start' }"))
        }
    }

    // Owner-picked site theme: light (default) / dark / auto (follows the guest's system).
    val themeClass =
        when (restaurant.theme) {
            "dark" -> "qk-dark"
            "auto" -> "qk-auto"
            else -> null
        }

    Box(
        Modifier
            .fillMaxWidth()
            .minHeight(100.vh)
            .attrsModifier {
                classes("qk-paper")
                themeClass?.let { classes(it) }
            },
        contentAlignment = Alignment.TopCenter,
    ) {
        Column(
            Modifier.fillMaxWidth().maxWidth(640.px).attrsModifier { classes("qk-fade-in") },
        ) {
            HeroHeader(
                restaurant = restaurant,
                lang = lang,
                tableLabel = tableLabel,
                onLangChange = { lang = it },
                onMenuOpen = { infoOpen = true },
                onRateClick = { rateOpen = true },
                showFilters = restaurant.menuShowFilters,
                filterCount = filters.size,
                onFilterClick = { filtersOpen = true },
            )

            CategoryChips(
                categories = state.menu.categories.map { it.category },
                thumbnails =
                    if (restaurant.menuCategoryThumbs) {
                        state.menu.categories.associate { block ->
                            block.category.slug to block.items.firstOrNull { it.photoUrl != null }?.photoUrl
                        }
                    } else {
                        emptyMap()
                    },
                activeSlug = activeCategory,
                lang = lang,
                accent = accent,
            )

            if (filters.isEmpty()) {
                if (restaurant.menuShowPopular && state.popular.isNotEmpty()) {
                    ShowcaseRow(
                        title = t.popular,
                        iconSvg = MenuIcons.Flame,
                        items = state.popular.map { it.withLiveLikes() },
                        lang = lang,
                        currency = restaurant.currency,
                        accent = accent,
                        onItemClick = { selectedDish = it },
                    )
                }
                if (restaurant.menuShowChef && state.featured.isNotEmpty()) {
                    ShowcaseRow(
                        title = t.chefRecommends,
                        iconSvg = MenuIcons.Sparkles,
                        items = state.featured.map { it.withLiveLikes() },
                        lang = lang,
                        currency = restaurant.currency,
                        accent = accent,
                        onItemClick = { selectedDish = it },
                    )
                }
            }

            fun matches(item: PublicMenuItemDto): Boolean =
                filters.all { filter ->
                    when (filter) {
                        "vegan" -> "vegan" in item.tags
                        "spicy" -> "spicy" in item.tags
                        "gluten-free" -> "gluten" !in item.allergens
                        else -> true
                    }
                }

            val anyResults = state.menu.categories.any { block -> block.items.any(::matches) }

            if (!anyResults) {
                EmptySearchView(lang) { filters = emptySet() }
            }

            state.menu.categories.forEachIndexed { index, categoryBlock ->
                val visibleItems = categoryBlock.items.filter(::matches)
                if (visibleItems.isNotEmpty()) {
                    CategorySection(
                        id = "cat-${categoryBlock.category.slug}",
                        index = index,
                        title = categoryBlock.category.name.localized(lang),
                        items = visibleItems.map { it.withLiveLikes() },
                        lang = lang,
                        currency = restaurant.currency,
                        accent = accent,
                        featureFirst = restaurant.menuFeatureFirst,
                        onItemClick = { selectedDish = it },
                    )
                }
            }

            AboutSection(restaurant = restaurant, lang = lang)

            PoweredByFooter(lang)
        }

        // Pills show whenever the venue enables them, even without a scanned table.
        // Off a table (tableLabel == null) the sheet nudges the guest to scan the
        // table QR instead of exposing the waiter/check actions.
        if (restaurant.waiterCallEnabled) {
            WaiterFab(slug = restaurant.slug, tableLabel = tableLabel, lang = lang, accent = accent)
        }
        if (restaurant.guestCheckEnabled) {
            CheckFab(
                slug = restaurant.slug,
                tableLabel = tableLabel,
                lang = lang,
                accent = accent,
                accentHex = accentHex,
                currency = restaurant.currency,
                // With no waiter pill below, drop the check pill to the base spot.
                bottomPx = if (restaurant.waiterCallEnabled) 68 else 16,
            )
        }

        if (rateOpen) {
            RatingSheet(restaurant = restaurant, lang = lang, onClose = { rateOpen = false })
        }

        if (filtersOpen) {
            FilterSheet(
                lang = lang,
                selected = filters,
                accent = accent,
                onToggle = { key ->
                    filters = if (key in filters) filters - key else filters + key
                },
                onReset = { filters = emptySet() },
                onClose = { filtersOpen = false },
            )
        }

        if (infoOpen) {
            InfoSheet(
                restaurant = restaurant,
                lang = lang,
                loyaltySignedIn = loyaltySignedIn,
                loyaltyInfo = loyaltyInfo,
                loyaltyQr = loyaltyQr,
                onLoyaltyInfo = { loyaltyInfo = it },
                onLoyaltyQr = { loyaltyQr = it },
                onLoyaltyAuthChange = { signedIn ->
                    loyaltySignedIn = signedIn
                    if (!signedIn) loyaltyQr = null
                },
                onClose = { infoOpen = false },
            )
        }

        selectedDish?.let { dish ->
            DishSheet(
                dish = dish.withLiveLikes(),
                lang = lang,
                currency = restaurant.currency,
                accent = accent,
                onClose = { selectedDish = null },
                onLikesChanged = { itemId, likes ->
                    likeOverrides = likeOverrides + (itemId to likes)
                },
            )
        }
    }
}

private data class FilterOption(val key: String, val iconSvg: String)

private val FILTER_OPTIONS =
    listOf(
        FilterOption("vegan", MenuIcons.Leaf),
        FilterOption("spicy", MenuIcons.Flame),
        FilterOption("gluten-free", MenuIcons.WheatOff),
    )

/** Bottom sheet to rate the restaurant 1..5; asks the guest to sign in first. */
@Composable
private fun RatingSheet(
    restaurant: PublicRestaurantDto,
    lang: String,
    onClose: () -> Unit,
) {
    val t = menuT(lang)
    // One vote per browser: if this browser already rated, show it read-only.
    val alreadyScore = remember { GuestSession.ratedScore(restaurant.id) }
    var selected by remember { mutableStateOf(alreadyScore ?: 0) }
    val locked = alreadyScore != null
    var busy by remember { mutableStateOf(false) }
    var done by remember { mutableStateOf(false) }
    var failed by remember { mutableStateOf(false) }

    LaunchedEffect(done) {
        if (done) {
            kotlinx.coroutines.delay(1600)
            onClose()
        }
    }

    fun submit(score: Int) {
        if (busy || locked) return
        selected = score
        busy = true
        failed = false
        MainScope().launch {
            if (GuestApi.rate("RESTAURANT", restaurant.id, score)) {
                GuestSession.setRated(restaurant.id, score)
                done = true
            } else {
                busy = false
                failed = true
            }
        }
    }

    Box(
        Modifier
            .position(Position.Fixed)
            .top(0.px).left(0.px).right(0.px).bottom(0.px)
            .backgroundColor(Color.rgba(15, 20, 17, 0.5f))
            .zIndex(60)
            .attrsModifier { classes("qk-backdrop") }
            .onClick { onClose() },
    )
    Box(
        Modifier
            .position(Position.Fixed)
            .left(0.px).right(0.px).bottom(0.px)
            .zIndex(61),
        contentAlignment = Alignment.BottomCenter,
    ) {
        Column(
            Modifier
                .fillMaxWidth()
                .maxWidth(640.px)
                .backgroundColor(Brand.Surface)
                .borderRadius(topLeft = Brand.RadiusSheet.px, topRight = Brand.RadiusSheet.px)
                .padding(left = 18.px, right = 18.px, top = 12.px, bottom = 24.px)
                .attrsModifier { classes("qk-sheet") },
        ) {
            Box(Modifier.fillMaxWidth(), contentAlignment = Alignment.Center) {
                Box(
                    Modifier.width(40.px).height(4.px)
                        .backgroundColor(Brand.Divider).borderRadius(2.px).margin(bottom = 14.px),
                )
            }

            if (done) {
                Column(
                    Modifier.fillMaxWidth().padding(topBottom = 20.px),
                    horizontalAlignment = Alignment.CenterHorizontally,
                ) {
                    Box(
                        Modifier.size(52.px).borderRadius(Brand.RadiusChip.px).backgroundColor(Brand.GreenSoft),
                        contentAlignment = Alignment.Center,
                    ) {
                        InlineIcon(MenuIcons.StarFilled, 24, "#E8A33D")
                    }
                    SpanText(
                        t.thanksRating,
                        Modifier
                            .fontSize(17.px)
                            .fontWeight(FontWeight.Bold)
                            .color(Brand.Charcoal)
                            .margin(top = 12.px)
                            .attrsModifier { classes("qk-display") },
                    )
                }
            } else {
                SpanText(
                    t.ratePlace,
                    Modifier
                        .fontSize(19.px)
                        .fontWeight(FontWeight.Bold)
                        .color(Brand.Charcoal)
                        .attrsModifier { classes("qk-display") },
                )
                SpanText(
                    restaurant.name,
                    Modifier.fontSize(13.px).color(Brand.TextSecondary).margin(top = 2.px, bottom = 14.px),
                )

                Row(
                    Modifier.fillMaxWidth().margin(topBottom = 6.px),
                    horizontalArrangement = Arrangement.Center,
                ) {
                    (1..5).forEach { score ->
                        Box(
                            Modifier
                                .size(46.px)
                                .then(if (locked) Modifier else Modifier.cursor(Cursor.Pointer))
                                .attrsModifier {
                                    if (!locked) {
                                        classes("qk-press")
                                        attr("role", "button")
                                        attr("aria-label", "$score")
                                    }
                                }
                                .onClick { submit(score) },
                            contentAlignment = Alignment.Center,
                        ) {
                            InlineIcon(
                                if (score <= selected) MenuIcons.StarFilled else MenuIcons.StarOutline,
                                32,
                                if (score <= selected) "#E8A33D" else "#C9C3BA",
                            )
                        }
                    }
                }
                SpanText(
                    when {
                        locked -> t.alreadyRated
                        failed -> t.submitFailed
                        busy -> "…"
                        else -> t.tapStar
                    },
                    Modifier
                        .fillMaxWidth()
                        .fontSize(12.px)
                        .color(if (failed) Brand.CoralText else Brand.TextSecondary)
                        .margin(top = 6.px)
                        .styleModifier { property("text-align", "center") },
                )
            }
        }
    }
}

@Composable
private fun FilterSheet(
    lang: String,
    selected: Set<String>,
    accent: Color,
    onToggle: (String) -> Unit,
    onReset: () -> Unit,
    onClose: () -> Unit,
) {
    val t = menuT(lang)
    Box(
        Modifier
            .position(Position.Fixed)
            .top(0.px).left(0.px).right(0.px).bottom(0.px)
            .backgroundColor(Color.rgba(15, 20, 17, 0.5f))
            .zIndex(60)
            .attrsModifier { classes("qk-backdrop") }
            .onClick { onClose() },
    )
    Box(
        Modifier
            .position(Position.Fixed)
            .left(0.px).right(0.px).bottom(0.px)
            .zIndex(61),
        contentAlignment = Alignment.BottomCenter,
    ) {
        Column(
            Modifier
                .fillMaxWidth()
                .maxWidth(640.px)
                .backgroundColor(Brand.Surface)
                .borderRadius(topLeft = Brand.RadiusSheet.px, topRight = Brand.RadiusSheet.px)
                .padding(left = 18.px, right = 18.px, top = 12.px, bottom = 22.px)
                .attrsModifier { classes("qk-sheet") },
        ) {
            Box(Modifier.fillMaxWidth(), contentAlignment = Alignment.Center) {
                Box(
                    Modifier.width(40.px).height(4.px)
                        .backgroundColor(Brand.Divider).borderRadius(2.px).margin(bottom = 14.px),
                )
            }
            Row(
                Modifier.fillMaxWidth().margin(bottom = 12.px),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically,
            ) {
                SpanText(
                    t.filters,
                    Modifier
                        .fontSize(19.px)
                        .fontWeight(FontWeight.Bold)
                        .color(Brand.Charcoal)
                        .attrsModifier { classes("qk-display") },
                )
                if (selected.isNotEmpty()) {
                    SpanText(
                        t.reset,
                        Modifier
                            .fontSize(13.px)
                            .fontWeight(FontWeight.SemiBold)
                            .cursor(Cursor.Pointer)
                            .styleModifier { property("color", "var(--qk-accent)") }
                            .attrsModifier { classes("qk-press"); attr("role", "button") }
                            .onClick { onReset() },
                    )
                }
            }

            FILTER_OPTIONS.forEach { option ->
                val active = option.key in selected
                Row(
                    Modifier
                        .fillMaxWidth()
                        .backgroundColor(if (active) Brand.WarmBackground else Color.rgba(0, 0, 0, 0f))
                        .borderRadius(12.px)
                        .padding(topBottom = 11.px, leftRight = 10.px)
                        .cursor(Cursor.Pointer)
                        .attrsModifier {
                            classes("qk-press")
                            attr("role", "checkbox")
                            attr("aria-checked", "$active")
                        }
                        .onClick { onToggle(option.key) },
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    Row(verticalAlignment = Alignment.CenterVertically) {
                        Box(
                            Modifier
                                .size(34.px)
                                .minWidth(34.px)
                                .borderRadius(Brand.RadiusChip.px)
                                .backgroundColor(Brand.WarmBackground)
                                .margin(right = 10.px)
                                .then(
                                    if (active) {
                                        Modifier.styleModifier { property("background", "var(--qk-accent)") }
                                    } else {
                                        Modifier
                                    },
                                ),
                            contentAlignment = Alignment.Center,
                        ) {
                            InlineIcon(option.iconSvg, 15, if (active) "#FFFFFF" else "#6B7280")
                        }
                        SpanText(
                            t.filterLabel(option.key),
                            Modifier
                                .fontSize(15.px)
                                .fontWeight(if (active) FontWeight.SemiBold else FontWeight.Medium)
                                .color(Brand.Charcoal),
                        )
                    }
                    if (active) {
                        InlineIcon(com.qrkontaq.ui.admin.AdminIcons.Check, 16, "var(--qk-accent)")
                    }
                }
            }

            Box(
                Modifier
                    .fillMaxWidth()
                    .margin(top = 14.px)
                    .backgroundColor(accent)
                    .borderRadius(14.px)
                    .padding(topBottom = 13.px)
                    .cursor(Cursor.Pointer)
                    .attrsModifier { classes("qk-press"); attr("role", "button") }
                    .onClick { onClose() },
                contentAlignment = Alignment.Center,
            ) {
                SpanText(
                    t.done,
                    Modifier.fontSize(15.px).fontWeight(FontWeight.SemiBold).color(Brand.Surface),
                )
            }
        }
    }
}

@Composable
private fun WaiterFab(
    slug: String,
    tableLabel: String?,
    lang: String,
    accent: Color,
) {
    val t = menuT(lang)
    var open by remember { mutableStateOf(false) }
    var sent by remember { mutableStateOf<String?>(null) } // call type that was just sent

    LaunchedEffect(sent) {
        if (sent != null) {
            kotlinx.coroutines.delay(2600)
            sent = null
            open = false
        }
    }

    // Floating pill
    Row(
        Modifier
            .position(Position.Fixed)
            .right(16.px)
            .bottom(16.px)
            .zIndex(40)
            .backgroundColor(accent)
            .borderRadius(Brand.RadiusChip.px)
            .padding(topBottom = 12.px, leftRight = 17.px)
            .cursor(Cursor.Pointer)
            .boxShadow(offsetY = 6.px, blurRadius = 20.px, color = Color.rgba(0, 0, 0, 0.28f))
            .attrsModifier { classes("qk-press"); attr("role", "button") }
            .onClick { open = true },
        verticalAlignment = Alignment.CenterVertically,
    ) {
        InlineIcon(MenuIcons.Bell, 16, "#FFFFFF")
        SpanText(
            t.waiter,
            Modifier.fontSize(14.px).fontWeight(FontWeight.SemiBold).color(Brand.Surface).margin(left = 7.px),
        )
    }

    if (open) {
        Box(
            Modifier
                .position(Position.Fixed)
                .top(0.px).left(0.px).right(0.px).bottom(0.px)
                .backgroundColor(Color.rgba(15, 20, 17, 0.5f))
                .zIndex(60)
                .attrsModifier { classes("qk-backdrop") }
                .onClick { if (sent == null) open = false },
        )
        Box(
            Modifier
                .position(Position.Fixed)
                .left(0.px).right(0.px).bottom(0.px)
                .zIndex(61),
            contentAlignment = Alignment.BottomCenter,
        ) {
            Column(
                Modifier
                    .fillMaxWidth()
                    .maxWidth(640.px)
                    .backgroundColor(Brand.Surface)
                    .borderRadius(topLeft = Brand.RadiusSheet.px, topRight = Brand.RadiusSheet.px)
                    .padding(left = 18.px, right = 18.px, top = 14.px, bottom = 22.px)
                    .attrsModifier { classes("qk-sheet") },
            ) {
                Box(Modifier.fillMaxWidth(), contentAlignment = Alignment.Center) {
                    Box(
                        Modifier.width(40.px).height(4.px)
                            .backgroundColor(Brand.Divider).borderRadius(2.px).margin(bottom = 14.px),
                    )
                }
                when {
                    tableLabel == null -> ScanTableNotice(lang)
                    sent != null -> {
                        Column(
                            Modifier.fillMaxWidth().padding(topBottom = 18.px),
                            horizontalAlignment = Alignment.CenterHorizontally,
                        ) {
                            Box(
                                Modifier.size(52.px).borderRadius(Brand.RadiusChip.px).backgroundColor(Brand.GreenSoft),
                                contentAlignment = Alignment.Center,
                            ) {
                                InlineIcon(MenuIcons.Bell, 22, "#2E9E5B")
                            }
                            SpanText(
                                t.waiterComing,
                                Modifier
                                    .fontSize(17.px)
                                    .fontWeight(FontWeight.Bold)
                                    .color(Brand.Charcoal)
                                    .margin(top = 12.px)
                                    .attrsModifier { classes("qk-display") },
                            )
                            SpanText(
                                t.table(tableLabel),
                                Modifier.fontSize(13.px).color(Brand.TextSecondary).margin(top = 4.px),
                            )
                        }
                    }
                    else -> {
                        SpanText(
                            t.table(tableLabel),
                            Modifier.fontSize(13.px).color(Brand.TextSecondary).margin(bottom = 10.px),
                        )
                        WaiterActionRow(
                            iconSvg = MenuIcons.Bell,
                            text = t.callWaiter,
                            accent = accent,
                        ) {
                            MainScope().launch {
                                if (GuestApi.callWaiter(slug, tableLabel, "WAITER")) sent = "WAITER"
                            }
                        }
                        WaiterActionRow(
                            iconSvg = MenuIcons.Receipt,
                            text = t.bringBill,
                            accent = accent,
                        ) {
                            MainScope().launch {
                                if (GuestApi.callWaiter(slug, tableLabel, "BILL")) sent = "BILL"
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun WaiterActionRow(
    iconSvg: String,
    text: String,
    accent: Color,
    onClick: () -> Unit,
) {
    Row(
        Modifier
            .fillMaxWidth()
            .backgroundColor(Brand.WarmBackground)
            .borderRadius(14.px)
            .padding(14.px)
            .margin(bottom = 8.px)
            .cursor(Cursor.Pointer)
            .attrsModifier { classes("qk-press"); attr("role", "button"); attr("tabindex", "0") }
            .onClick { onClick() },
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Box(
            Modifier
                .size(38.px)
                .minWidth(38.px)
                .borderRadius(Brand.RadiusChip.px)
                .backgroundColor(accent)
                .margin(right = 11.px),
            contentAlignment = Alignment.Center,
        ) {
            InlineIcon(iconSvg, 17, "#FFFFFF")
        }
        SpanText(
            text,
            Modifier.fontSize(15.px).fontWeight(FontWeight.SemiBold).color(Brand.Charcoal),
        )
    }
}

/**
 * Placeholder shown inside the waiter/check sheets when the guest opened the menu
 * without scanning a table (no table label). Nudges them to scan the table QR so
 * the call/check has a table to attach to.
 */
@Composable
private fun ScanTableNotice(lang: String) {
    val t = menuT(lang)
    Column(
        Modifier.fillMaxWidth().padding(topBottom = 18.px),
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        Box(
            Modifier.size(52.px).borderRadius(Brand.RadiusChip.px).backgroundColor(Brand.WarmBackground),
            contentAlignment = Alignment.Center,
        ) {
            InlineIcon(MenuIcons.QrCode, 24, "#9AA39D")
        }
        SpanText(
            t.scanTableTitle,
            Modifier
                .fontSize(17.px)
                .fontWeight(FontWeight.Bold)
                .color(Brand.Charcoal)
                .margin(top = 12.px)
                .attrsModifier { classes("qk-display") },
        )
        SpanText(
            t.scanTableHint,
            Modifier
                .fontSize(13.px)
                .color(Brand.TextSecondary)
                .margin(top = 5.px)
                .maxWidth(300.px)
                .textAlign(TextAlign.Center),
        )
    }
}

/**
 * A floating "my check" pill (above the waiter pill) that opens a bottom sheet
 * with the table's live receipt. The check is fetched the moment the sheet opens
 * and re-polled every few seconds while it stays open, so a waiter creating or
 * editing the order is reflected without the diner reopening the sheet.
 */
@Composable
private fun CheckFab(
    slug: String,
    tableLabel: String?,
    lang: String,
    accent: Color,
    accentHex: String,
    currency: String,
    bottomPx: Int,
) {
    val t = menuT(lang)
    val container = LocalAppContainer.current
    val repository = remember { PublicRepository(container.defaultHttpClient) }

    var open by remember { mutableStateOf(false) }
    var loading by remember { mutableStateOf(false) }
    var check by remember { mutableStateOf<PublicTableCheckDto?>(null) }

    // Fetch on open, then keep polling while the sheet is visible. Relaunches when
    // [open] flips; the coroutine is cancelled automatically when it closes.
    LaunchedEffect(open) {
        if (!open || tableLabel == null) return@LaunchedEffect
        if (check == null) loading = true
        while (true) {
            val fresh = runCatching { repository.tableCheck(slug, tableLabel) }.getOrNull()
            if (fresh != null) check = fresh
            loading = false
            kotlinx.coroutines.delay(5000)
        }
    }

    // Floating pill — surface with an accent outline so it reads as secondary to
    // the solid-accent waiter pill sitting just below it.
    Row(
        Modifier
            .position(Position.Fixed)
            .right(16.px)
            .bottom(bottomPx.px)
            .zIndex(40)
            .backgroundColor(Brand.Surface)
            .border(1.px, LineStyle.Solid, accent)
            .borderRadius(Brand.RadiusChip.px)
            .padding(topBottom = 11.px, leftRight = 16.px)
            .cursor(Cursor.Pointer)
            .boxShadow(offsetY = 6.px, blurRadius = 18.px, color = Color.rgba(0, 0, 0, 0.16f))
            .attrsModifier { classes("qk-press"); attr("role", "button") }
            .onClick { open = true },
        verticalAlignment = Alignment.CenterVertically,
    ) {
        InlineIcon(MenuIcons.Receipt, 16, accentHex)
        SpanText(
            t.myCheck,
            Modifier.fontSize(14.px).fontWeight(FontWeight.SemiBold).color(accent).margin(left = 7.px),
        )
    }

    if (open) {
        Box(
            Modifier
                .position(Position.Fixed)
                .top(0.px).left(0.px).right(0.px).bottom(0.px)
                .backgroundColor(Color.rgba(15, 20, 17, 0.5f))
                .zIndex(60)
                .attrsModifier { classes("qk-backdrop") }
                .onClick { open = false },
        )
        Box(
            Modifier
                .position(Position.Fixed)
                .left(0.px).right(0.px).bottom(0.px)
                .zIndex(61),
            contentAlignment = Alignment.BottomCenter,
        ) {
            Column(
                Modifier
                    .fillMaxWidth()
                    .maxWidth(640.px)
                    .backgroundColor(Brand.Surface)
                    .borderRadius(topLeft = Brand.RadiusSheet.px, topRight = Brand.RadiusSheet.px)
                    .padding(left = 18.px, right = 18.px, top = 14.px, bottom = 22.px)
                    .attrsModifier { classes("qk-sheet") },
            ) {
                Box(Modifier.fillMaxWidth(), contentAlignment = Alignment.Center) {
                    Box(
                        Modifier.width(40.px).height(4.px)
                            .backgroundColor(Brand.Divider).borderRadius(2.px).margin(bottom = 14.px),
                    )
                }

                SpanText(
                    t.myCheck,
                    Modifier
                        .fontSize(19.px)
                        .fontWeight(FontWeight.Bold)
                        .color(Brand.Charcoal)
                        .attrsModifier { classes("qk-display") },
                )

                if (tableLabel == null) {
                    ScanTableNotice(lang)
                    return@Column
                }

                SpanText(
                    t.table(tableLabel),
                    Modifier.fontSize(13.px).color(Brand.TextSecondary).margin(top = 3.px, bottom = 12.px),
                )

                val current = check
                when {
                    loading && current == null ->
                        SpanText(
                            t.loading,
                            Modifier.fillMaxWidth().padding(topBottom = 26.px)
                                .textAlign(TextAlign.Center)
                                .fontSize(14.px).color(Brand.TextSecondary),
                        )

                    current == null || current.isEmpty ->
                        Column(
                            Modifier.fillMaxWidth().padding(topBottom = 22.px),
                            horizontalAlignment = Alignment.CenterHorizontally,
                        ) {
                            Box(
                                Modifier.size(52.px).borderRadius(Brand.RadiusChip.px)
                                    .backgroundColor(Brand.WarmBackground),
                                contentAlignment = Alignment.Center,
                            ) {
                                InlineIcon(MenuIcons.Receipt, 22, "#9AA39D")
                            }
                            SpanText(
                                t.noOrdersYet,
                                Modifier.fontSize(16.px).fontWeight(FontWeight.Bold)
                                    .color(Brand.Charcoal).margin(top = 12.px),
                            )
                            SpanText(
                                t.checkHint,
                                Modifier.fontSize(13.px).color(Brand.TextSecondary)
                                    .margin(top = 5.px).maxWidth(300.px)
                                    .textAlign(TextAlign.Center),
                            )
                        }

                    else -> {
                        val cur = current.currency.ifBlank { currency }
                        Column(
                            Modifier.fillMaxWidth().maxHeight(60.vh).overflow(Overflow.Auto),
                        ) {
                            current.orders.forEach { order ->
                                order.items.forEach { item ->
                                    Row(
                                        Modifier.fillMaxWidth().padding(topBottom = 9.px),
                                        verticalAlignment = Alignment.CenterVertically,
                                    ) {
                                        Box(
                                            Modifier.minWidth(30.px).height(24.px)
                                                .borderRadius(7.px).backgroundColor(Brand.WarmBackground)
                                                .padding(leftRight = 7.px).margin(right = 10.px),
                                            contentAlignment = Alignment.Center,
                                        ) {
                                            SpanText(
                                                "${item.qty}×",
                                                Modifier.fontSize(12.px).fontWeight(FontWeight.Bold)
                                                    .color(Brand.Charcoal),
                                            )
                                        }
                                        SpanText(
                                            item.name,
                                            Modifier.weight(1f).fontSize(14.px)
                                                .color(Brand.Charcoal).margin(right = 10.px),
                                        )
                                        SpanText(
                                            formatPrice(item.priceMinor * item.qty, cur),
                                            Modifier.fontSize(14.px).fontWeight(FontWeight.SemiBold)
                                                .color(Brand.Charcoal),
                                        )
                                    }
                                }
                            }
                        }

                        Box(
                            Modifier.fillMaxWidth().height(1.px)
                                .backgroundColor(Brand.Divider).margin(topBottom = 10.px),
                        )
                        Row(
                            Modifier.fillMaxWidth(),
                            verticalAlignment = Alignment.CenterVertically,
                        ) {
                            SpanText(
                                t.total,
                                Modifier.weight(1f).fontSize(16.px).fontWeight(FontWeight.Bold)
                                    .color(Brand.Charcoal),
                            )
                            SpanText(
                                formatPrice(current.totalMinor, cur),
                                Modifier.fontSize(18.px).fontWeight(FontWeight.Bold).color(accent)
                                    .attrsModifier { classes("qk-display") },
                            )
                        }
                        SpanText(
                            t.updatesLive,
                            Modifier.fillMaxWidth().margin(top = 10.px)
                                .fontSize(11.px).color(Brand.TextSecondary)
                                .textAlign(TextAlign.Center),
                        )
                    }
                }
            }
        }
    }
}

@Composable
private fun HeroHeader(
    restaurant: PublicRestaurantDto,
    lang: String,
    tableLabel: String?,
    onLangChange: (String) -> Unit,
    onMenuOpen: () -> Unit,
    onRateClick: () -> Unit,
    showFilters: Boolean,
    filterCount: Int,
    onFilterClick: () -> Unit,
) {
    val t = menuT(lang)
    Box(
        Modifier
            .fillMaxWidth()
            .height(if (restaurant.coverUrl != null) 280.px else 200.px)
            .position(Position.Relative),
    ) {
        restaurant.coverUrl?.let { cover ->
            Image(
                src = cover,
                modifier = Modifier
                    .fillMaxSize()
                    .objectFit(ObjectFit.Cover)
                    .attrsModifier { attr("alt", restaurant.name) },
            )
        } ?: Box(Modifier.fillMaxSize().attrsModifier { classes("qk-cover-fallback") })

        // Editorial scrim: darker at the bottom where the restaurant identity lives.
        Box(Modifier.fillMaxSize().attrsModifier { classes("qk-hero-grad") })

        // Square borderless logo, pinned to the top-left corner.
        restaurant.logoUrl?.let { logo ->
            Image(
                src = logo,
                modifier = Modifier
                    .align(Alignment.TopStart)
                    .margin(14.px)
                    .size(58.px)
                    .minWidth(58.px)
                    .borderRadius(14.px)
                    .objectFit(ObjectFit.Cover)
                    .zIndex(2)
                    // drop-shadow (not box-shadow) so the shadow hugs the logo's alpha
                    // channel: round logos get a round shadow, not a square plate.
                    .styleModifier { property("filter", "drop-shadow(0 3px 9px rgba(0,0,0,.35))") }
                    .attrsModifier { attr("alt", restaurant.name) },
            )
        }

        // Top-right controls: language + burger menu.
        Row(
            Modifier.align(Alignment.TopEnd).margin(14.px).zIndex(3).gap(8.px),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            LangControl(restaurant.languages, lang, onLangChange)
            Box(
                Modifier
                    .size(40.px)
                    .minWidth(40.px)
                    .borderRadius(Brand.RadiusChip.px)
                    .cursor(Cursor.Pointer)
                    .styleModifier {
                        property("background", "rgba(255,255,255,.92)")
                        property("backdrop-filter", "blur(10px)")
                        property("box-shadow", "0 2px 10px rgba(31,36,33,.18)")
                    }
                    .attrsModifier { classes("qk-press"); attr("role", "button"); attr("aria-label", "Menu") }
                    .onClick { onMenuOpen() },
                contentAlignment = Alignment.Center,
            ) {
                InlineIcon(MenuIcons.Burger, 18, "#1F2421", extraClass = "qk-ink")
            }
        }

        Column(
            Modifier
                .position(Position.Absolute)
                .left(0.px).right(0.px).bottom(0.px)
                .padding(left = 18.px, right = 18.px, bottom = 18.px),
        ) {
            Column(Modifier.fillMaxWidth()) {
                SpanText(
                    restaurant.name,
                    Modifier
                        .fontSize(23.px)
                        .color(Brand.Surface)
                        .styleModifier { property("line-height", "1.25") }
                        .attrsModifier { classes("qk-brandname", "qk-clamp2") },
                )
                restaurant.address?.let { address ->
                    Row(Modifier.margin(top = 6.px), verticalAlignment = Alignment.CenterVertically) {
                        InlineIcon(MenuIcons.MapPin, 12, "rgba(255,255,255,.85)")
                        SpanText(
                            address,
                            Modifier
                                .fontSize(13.px)
                                .color(Color.rgba(255, 255, 255, 0.85f))
                                .margin(left = 5.px),
                        )
                    }
                }
            }

            Row(
                Modifier.fillMaxWidth().margin(top = 12.px).flexWrap(FlexWrap.Wrap).gap(8.px)
                    // Keep the chips clear of the filters button in the corner.
                    .then(if (showFilters) Modifier.padding(right = 46.px) else Modifier),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                openInfo(restaurant.workingHours)?.let { info -> OpenStatusChip(info, lang) }

                restaurant.rating.average?.let { avg ->
                    val rounded = (avg * 10).toInt() / 10.0
                    Box(
                        Modifier
                            .cursor(Cursor.Pointer)
                            .attrsModifier {
                                classes("qk-press")
                                attr("role", "button")
                                attr("aria-label", t.ratePlace)
                            }
                            .onClick { onRateClick() },
                    ) {
                        GlassChip {
                            InlineIcon(MenuIcons.StarFilled, 13, "#FFC862")
                            SpanText(
                                "$rounded" + if (restaurant.rating.count > 0) " (${restaurant.rating.count})" else "",
                                Modifier
                                    .fontSize(12.px)
                                    .fontWeight(FontWeight.SemiBold)
                                    .color(Brand.Surface)
                                    .margin(left = 5.px),
                            )
                        }
                    }
                }

                tableLabel?.let { label ->
                    GlassChip {
                        SpanText(
                            t.table(label),
                            Modifier.fontSize(12.px).fontWeight(FontWeight.SemiBold).color(Brand.Surface),
                        )
                    }
                }
            }
        }

        // Filters button pinned to the banner's bottom-right corner.
        if (showFilters) {
            val hasFilters = filterCount > 0
            Row(
                Modifier
                    .align(Alignment.BottomEnd)
                    .margin(right = 14.px, bottom = 18.px)
                    .zIndex(3)
                    .minWidth(40.px)
                    .height(40.px)
                    .borderRadius(Brand.RadiusChip.px)
                    .padding(leftRight = if (hasFilters) 12.px else 0.px)
                    .cursor(Cursor.Pointer)
                    .styleModifier {
                        if (hasFilters) {
                            property("background", "var(--qk-accent)")
                        } else {
                            property("background", "rgba(255,255,255,.92)")
                            property("backdrop-filter", "blur(10px)")
                        }
                        property("box-shadow", "0 2px 10px rgba(31,36,33,.18)")
                    }
                    .attrsModifier {
                        classes("qk-press")
                        attr("role", "button")
                        attr("aria-label", t.filters)
                    }
                    .onClick { onFilterClick() },
                horizontalArrangement = Arrangement.Center,
                verticalAlignment = Alignment.CenterVertically,
            ) {
                InlineIcon(
                    MenuIcons.Filter,
                    16,
                    if (hasFilters) "#FFFFFF" else "#1F2421",
                    extraClass = if (hasFilters) null else "qk-ink",
                )
                if (hasFilters) {
                    SpanText(
                        "$filterCount",
                        Modifier
                            .fontSize(12.px)
                            .fontWeight(FontWeight.Bold)
                            .color(Brand.Surface)
                            .margin(left = 5.px),
                    )
                }
            }
        }
    }
}

@Composable
private fun GlassChip(content: @Composable () -> Unit) {
    Row(
        Modifier
            .borderRadius(Brand.RadiusChip.px)
            .padding(topBottom = 5.px, leftRight = 12.px)
            .attrsModifier { classes("qk-glass") },
        verticalAlignment = Alignment.CenterVertically,
    ) {
        content()
    }
}

@Composable
private fun OpenStatusChip(info: OpenInfo, lang: String) {
    val t = menuT(lang)
    val text =
        when {
            info.isOpen && info.boundary != null -> t.openUntil(info.boundary)
            info.isOpen -> t.open
            info.boundary != null -> t.closedOpens(info.boundary)
            else -> t.closed
        }
    GlassChip {
        Box(
            Modifier
                .size(7.px)
                .borderRadius(Brand.RadiusChip.px)
                .backgroundColor(if (info.isOpen) Brand.OpenDot else Brand.ClosedDot)
                .margin(right = 6.px),
        )
        SpanText(
            text,
            Modifier
                .fontSize(12.px)
                .fontWeight(FontWeight.SemiBold)
                .color(Brand.Surface),
        )
    }
}

/**
 * "About us" block rendered after the menu itself. Not a menu category —
 * it lives outside the category list, chips and scroll-spy.
 */
@Composable
private fun AboutSection(restaurant: PublicRestaurantDto, lang: String) {
    val t = menuT(lang)
    val socials = restaurant.socials.filterKeys { it != "website" }
    val hasHours = restaurant.workingHours.values.any { it != null }
    val hasAnything =
        !restaurant.address.isNullOrBlank() || !restaurant.phone.isNullOrBlank() ||
            socials.isNotEmpty() || hasHours
    if (!hasAnything) return

    var scheduleOpen by remember { mutableStateOf(false) }

    val mapsUrl =
        when {
            restaurant.lat != null && restaurant.lng != null ->
                "https://www.google.com/maps/search/?api=1&query=${restaurant.lat},${restaurant.lng}"
            !restaurant.address.isNullOrBlank() ->
                "https://www.google.com/maps/search/?api=1&query=" +
                    js("encodeURIComponent")(restaurant.address) as String
            else -> null
        }

    Column(
        Modifier.fillMaxWidth().padding(leftRight = 16.px).attrsModifier { classes("qk-section") },
    ) {
        SpanText(
            t.aboutUs,
            Modifier
                .fontSize(23.px)
                .fontWeight(FontWeight.Bold)
                .color(Brand.Charcoal)
                .margin(top = 34.px, bottom = 14.px)
                .styleModifier { property("line-height", "0.95") }
                .attrsModifier { classes("qk-display") },
        )

        Column(
            Modifier
                .fillMaxWidth()
                .backgroundColor(Brand.Surface)
                .borderRadius(20.px)
                .border(1.px, LineStyle.Solid, Brand.Divider)
                .padding(18.px)
                .boxShadow(offsetY = 6.px, blurRadius = 20.px, color = Color.rgba(31, 36, 33, 0.06f))
                .attrsModifier { classes("qk-rise") },
        ) {
            restaurant.address?.takeIf { it.isNotBlank() }?.let { address ->
                AboutRow(MenuIcons.MapPin, t.addressLabel) {
                    SpanText(
                        address,
                        Modifier.fontSize(14.px).color(Brand.Charcoal)
                            .styleModifier { property("line-height", "1.45") },
                    )
                }
            }

            restaurant.phone?.takeIf { it.isNotBlank() }?.let { phone ->
                AboutRow(MenuIcons.Phone, t.phoneLabel) {
                    A(href = "tel:$phone", attrs = { classes("qk-plain") }) {
                        SpanText(
                            phone,
                            Modifier.fontSize(14.px).fontWeight(FontWeight.SemiBold)
                                .styleModifier { property("color", "var(--qk-accent)") },
                        )
                    }
                }
            }

            if (hasHours) {
                AboutRow(MenuIcons.Clock, t.workingHours) {
                    val todayKey = listOf("sun", "mon", "tue", "wed", "thu", "fri", "sat")[kotlin.js.Date().getDay()]
                    val today = restaurant.workingHours[todayKey]
                    Row(verticalAlignment = Alignment.CenterVertically) {
                        SpanText(
                            today?.let { "${it.open} – ${it.close}" } ?: t.closedToday,
                            Modifier.fontSize(14.px).color(Brand.Charcoal),
                        )
                        SpanText(
                            t.schedule,
                            Modifier
                                .fontSize(13.px)
                                .fontWeight(FontWeight.SemiBold)
                                .margin(left = 10.px)
                                .cursor(Cursor.Pointer)
                                .styleModifier { property("color", "var(--qk-accent)") }
                                .attrsModifier {
                                    classes("qk-press")
                                    attr("role", "button")
                                    attr("aria-expanded", "$scheduleOpen")
                                }
                                .onClick { scheduleOpen = !scheduleOpen },
                        )
                    }
                    if (scheduleOpen) {
                        Column(Modifier.fillMaxWidth().maxWidth(220.px).margin(top = 8.px)) {
                            val dayLabels =
                                listOf("mon", "tue", "wed", "thu", "fri", "sat", "sun").zip(t.daysShort)
                            dayLabels.forEach { (key, label) ->
                                val hours = restaurant.workingHours[key]
                                val isToday = key == todayKey
                                Row(
                                    Modifier.fillMaxWidth().margin(bottom = 3.px),
                                    horizontalArrangement = Arrangement.SpaceBetween,
                                ) {
                                    SpanText(
                                        label,
                                        Modifier
                                            .fontSize(13.px)
                                            .fontWeight(if (isToday) FontWeight.Bold else FontWeight.Normal)
                                            .color(if (isToday) Brand.Charcoal else Brand.TextSecondary),
                                    )
                                    SpanText(
                                        hours?.let { "${it.open} – ${it.close}" } ?: t.closedDay,
                                        Modifier
                                            .fontSize(13.px)
                                            .fontWeight(if (isToday) FontWeight.Bold else FontWeight.Normal)
                                            .color(if (isToday) Brand.Charcoal else Brand.TextSecondary),
                                    )
                                }
                            }
                        }
                    }
                }
            }

            if (socials.isNotEmpty()) {
                AboutRow(MenuIcons.Share, t.followUs) {
                    Row(Modifier.flexWrap(FlexWrap.Wrap).gap(6.px).margin(top = 2.px)) {
                        socials.forEach { (key, url) ->
                            val label =
                                when (key.lowercase()) {
                                    "facebook" -> "Facebook"
                                    "instagram" -> "Instagram"
                                    "tripadvisor" -> "TripAdvisor"
                                    "tiktok" -> "TikTok"
                                    "youtube" -> "YouTube"
                                    else -> key.replaceFirstChar { it.uppercase() }
                                }
                            A(href = url, attrs = { classes("qk-plain"); attr("target", "_blank") }) {
                                Row(
                                    Modifier
                                        .backgroundColor(Brand.WarmBackground)
                                        .borderRadius(999.px)
                                        .padding(topBottom = 7.px, leftRight = 13.px)
                                        .attrsModifier { classes("qk-press") },
                                    verticalAlignment = Alignment.CenterVertically,
                                ) {
                                    InlineIcon(com.qrkontaq.ui.admin.AdminIcons.ExternalLink, 12, "#6B7280")
                                    SpanText(
                                        label,
                                        Modifier.fontSize(13.px).fontWeight(FontWeight.Medium)
                                            .color(Brand.Charcoal).margin(left = 6.px),
                                    )
                                }
                            }
                        }
                    }
                }
            }

            mapsUrl?.let { url ->
                // Leaflet map preview with a logo pin, then the route button.
                TenantMap(
                    lat = restaurant.lat,
                    lng = restaurant.lng,
                    address = restaurant.address,
                    logoUrl = restaurant.logoUrl,
                    accentHex = Brand.accentHexFrom(restaurant.accentColor),
                )
                Box(Modifier.fillMaxWidth().margin(top = 12.px), contentAlignment = Alignment.Center) {
                    A(href = url, attrs = { classes("qk-plain"); attr("target", "_blank") }) {
                        Row(
                            Modifier
                                .borderRadius(Brand.RadiusChip.px)
                                .padding(topBottom = 12.px, leftRight = 26.px)
                                .styleModifier { property("background", "var(--qk-accent)") }
                                .attrsModifier { classes("qk-press"); attr("role", "button") },
                            verticalAlignment = Alignment.CenterVertically,
                        ) {
                            InlineIcon(MenuIcons.Navigation, 15, "#FFFFFF")
                            SpanText(
                                t.getDirections,
                                Modifier.fontSize(14.px).fontWeight(FontWeight.SemiBold)
                                    .color(Brand.Surface).margin(left = 8.px)
                                    .attrsModifier { classes("qk-nowrap") },
                            )
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun AboutRow(
    iconSvg: String,
    label: String,
    content: @Composable () -> Unit,
) {
    Row(Modifier.fillMaxWidth().margin(bottom = 14.px)) {
        Box(
            Modifier
                .size(36.px)
                .minWidth(36.px)
                .borderRadius(999.px)
                .backgroundColor(Brand.WarmBackground)
                .margin(right = 12.px),
            contentAlignment = Alignment.Center,
        ) {
            InlineIcon(iconSvg, 16, "#6B7280")
        }
        Column(Modifier.fillMaxWidth()) {
            SpanText(
                label,
                Modifier.fontSize(11.px).fontWeight(FontWeight.SemiBold)
                    .color(Brand.TextSecondary).margin(bottom = 2.px)
                    .styleModifier {
                        property("text-transform", "uppercase")
                        property("letter-spacing", "0.06em")
                    },
            )
            content()
        }
    }
}

/** Side drawer behind the burger: identity, loyalty, quick actions, grouped info. */
@Composable
private fun InfoSheet(
    restaurant: PublicRestaurantDto,
    lang: String,
    loyaltySignedIn: Boolean,
    loyaltyInfo: GuestApi.LoyaltyInfo?,
    loyaltyQr: GuestApi.LoyaltyQr?,
    onLoyaltyInfo: (GuestApi.LoyaltyInfo?) -> Unit,
    onLoyaltyQr: (GuestApi.LoyaltyQr?) -> Unit,
    onLoyaltyAuthChange: (Boolean) -> Unit,
    onClose: () -> Unit,
) {
    val t = menuT(lang)
    var hoursOpen by remember { mutableStateOf(false) }
    var allergensOpen by remember { mutableStateOf(false) }
    var companyOpen by remember { mutableStateOf(false) }

    Box(
        Modifier
            .position(Position.Fixed)
            .top(0.px).left(0.px).right(0.px).bottom(0.px)
            .backgroundColor(Color.rgba(15, 20, 17, 0.5f))
            .zIndex(60)
            .attrsModifier { classes("qk-backdrop") }
            .onClick { onClose() },
    )
    Column(
        Modifier
            .position(Position.Fixed)
            .top(0.px).right(0.px).bottom(0.px)
            .width(340.px)
            .maxWidth(88.percent)
            .backgroundColor(Brand.WarmBackground)
            .zIndex(61)
            .padding(16.px)
            .overflow { y(Overflow.Auto) }
            .attrsModifier { classes("qk-drawer", "qk-hide-scrollbar") },
    ) {
        // Identity header
        Row(
            Modifier.fillMaxWidth().margin(bottom = 16.px),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Row(Modifier.fillMaxWidth().margin(right = 8.px), verticalAlignment = Alignment.CenterVertically) {
                restaurant.logoUrl?.let { logo ->
                    Image(
                        src = logo,
                        modifier = Modifier
                            .size(46.px)
                            .minWidth(46.px)
                            .borderRadius(12.px)
                            .objectFit(ObjectFit.Cover)
                            .margin(right = 11.px)
                            .attrsModifier { attr("alt", restaurant.name) },
                    )
                }
                Column {
                    SpanText(
                        restaurant.name,
                        Modifier
                            .fontSize(15.px)
                            .color(Brand.Charcoal)
                            .styleModifier { property("font-family", "'Unbounded', system-ui, sans-serif") }
                            .attrsModifier { classes("qk-clamp1") },
                    )
                    openInfo(restaurant.workingHours)?.let { info ->
                        Row(Modifier.margin(top = 3.px), verticalAlignment = Alignment.CenterVertically) {
                            Box(
                                Modifier.size(7.px).borderRadius(Brand.RadiusChip.px)
                                    .backgroundColor(if (info.isOpen) Brand.Green else Brand.CoralText)
                                    .margin(right = 5.px),
                            )
                            SpanText(
                                when {
                                    info.isOpen && info.boundary != null -> t.openUntilPlain(info.boundary)
                                    info.isOpen -> t.open
                                    else -> t.closed
                                },
                                Modifier.fontSize(12.px).color(Brand.TextSecondary),
                            )
                        }
                    }
                }
            }
            Box(
                Modifier
                    .size(32.px)
                    .minWidth(32.px)
                    .borderRadius(Brand.RadiusChip.px)
                    .backgroundColor(Brand.Surface)
                    .cursor(Cursor.Pointer)
                    .attrsModifier { classes("qk-press"); attr("role", "button"); attr("aria-label", "Close") }
                    .onClick { onClose() },
                contentAlignment = Alignment.Center,
            ) {
                InlineIcon(MenuIcons.Close, 14, "#6B7280")
            }
        }

        // Loyalty: teaser + sign-in, or the live cashback QR once signed in.
        if (restaurant.loyaltyEnabled) {
            LoyaltySection(
                restaurant = restaurant,
                lang = lang,
                signedIn = loyaltySignedIn,
                info = loyaltyInfo,
                qr = loyaltyQr,
                onInfo = onLoyaltyInfo,
                onQr = onLoyaltyQr,
                onAuthChange = onLoyaltyAuthChange,
            )
        }

        // Quick actions
        val mapsUrl =
            when {
                restaurant.lat != null && restaurant.lng != null ->
                    "https://www.google.com/maps/search/?api=1&query=${restaurant.lat},${restaurant.lng}"
                restaurant.address != null ->
                    "https://www.google.com/maps/search/?api=1&query=" +
                        js("encodeURIComponent")(restaurant.address) as String
                else -> null
            }
        // Contacts group
        DrawerGroup(t.contacts) {
            restaurant.address?.let { address ->
                DrawerItem(
                    iconSvg = MenuIcons.MapPin,
                    label = address,
                    external = mapsUrl != null,
                    onClick = mapsUrl?.let { url -> { window.open(url, "_blank") } },
                )
            }
            restaurant.phone?.let { phone ->
                DrawerItem(
                    iconSvg = MenuIcons.Phone,
                    label = phone,
                    accented = true,
                    onClick = { window.location.href = "tel:$phone" },
                )
            }
            restaurant.socials["website"]?.let { site ->
                DrawerItem(
                    iconSvg = MenuIcons.Globe,
                    label = t.visitWebsite,
                    accented = true,
                    external = true,
                    onClick = { window.open(site, "_blank") },
                )
            }
        }

        // Socials group
        val socials = restaurant.socials.filterKeys { it != "website" }
        if (socials.isNotEmpty()) {
            DrawerGroup(t.followUs) {
                socials.entries.forEachIndexed { index, (key, url) ->
                    val label =
                        when (key.lowercase()) {
                            "facebook" -> "Facebook"
                            "instagram" -> "Instagram"
                            "tripadvisor" -> "TripAdvisor"
                            "tiktok" -> "TikTok"
                            "youtube" -> "YouTube"
                            else -> key.replaceFirstChar { it.uppercase() }
                        }
                    DrawerItem(
                        iconSvg = com.qrkontaq.ui.admin.AdminIcons.ExternalLink,
                        label = label,
                        external = true,
                        onClick = { window.open(url, "_blank") },
                    )
                }
            }
        }

        // Info group: hours, allergens, company
        DrawerGroup(t.information) {
            if (restaurant.workingHours.values.any { it != null }) {
                val todayKey = listOf("sun", "mon", "tue", "wed", "thu", "fri", "sat")[kotlin.js.Date().getDay()]
                val today = restaurant.workingHours[todayKey]
                DrawerItem(
                    iconSvg = MenuIcons.Clock,
                    label = t.workingHours,
                    sub = today?.let { "${t.today}: ${it.open} – ${it.close}" }
                        ?: "${t.today}: ${t.closedDay}",
                    expandable = true,
                    expanded = hoursOpen,
                    onClick = { hoursOpen = !hoursOpen },
                )
                if (hoursOpen) {
                    DrawerDetail {
                        val dayLabels =
                            listOf("mon", "tue", "wed", "thu", "fri", "sat", "sun").zip(t.daysShort)
                        dayLabels.forEach { (key, label) ->
                            val hours = restaurant.workingHours[key]
                            val isToday = key == todayKey
                            Row(
                                Modifier.fillMaxWidth().margin(bottom = 3.px),
                                horizontalArrangement = Arrangement.SpaceBetween,
                            ) {
                                SpanText(
                                    label,
                                    Modifier
                                        .fontSize(13.px)
                                        .fontWeight(if (isToday) FontWeight.Bold else FontWeight.Normal)
                                        .color(if (isToday) Brand.Charcoal else Brand.TextSecondary),
                                )
                                SpanText(
                                    hours?.let { "${it.open} – ${it.close}" } ?: t.closedDay,
                                    Modifier
                                        .fontSize(13.px)
                                        .fontWeight(if (isToday) FontWeight.Bold else FontWeight.Normal)
                                        .color(if (isToday) Brand.Charcoal else Brand.TextSecondary),
                                )
                            }
                        }
                    }
                }
            }
            DrawerItem(
                iconSvg = MenuIcons.Info,
                label = t.allergenList,
                expandable = true,
                expanded = allergensOpen,
                onClick = { allergensOpen = !allergensOpen },
            )
            if (allergensOpen) {
                DrawerDetail {
                    val allergens =
                        listOf("gluten", "lactose", "nuts", "eggs", "fish", "soy").map { t.allergen(it) }
                    SpanText(
                        t.allergenIntro + allergens.joinToString(", ") + ".",
                        Modifier.fontSize(13.px).color(Brand.TextSecondary)
                            .styleModifier { property("line-height", "1.5") },
                    )
                }
            }
            DrawerItem(
                iconSvg = MenuIcons.Utensils,
                label = t.aboutCompany,
                expandable = true,
                expanded = companyOpen,
                onClick = { companyOpen = !companyOpen },
            )
            if (companyOpen) {
                DrawerDetail {
                    SpanText(
                        restaurant.name,
                        Modifier.fontSize(13.px).fontWeight(FontWeight.SemiBold).color(Brand.Charcoal),
                    )
                    val description = restaurant.description.localized(lang)
                    if (description.isNotEmpty()) {
                        SpanText(
                            description,
                            Modifier.fontSize(13.px).color(Brand.TextSecondary).margin(top = 3.px)
                                .styleModifier { property("line-height", "1.5") },
                        )
                    }
                    restaurant.address?.let {
                        SpanText(it, Modifier.fontSize(12.px).color(Brand.TextSecondary).margin(top = 3.px))
                    }
                }
            }
        }

        // Legal group
        DrawerGroup(t.legal) {
            listOf(
                t.cookiePolicy to "https://qrkontaq.com/cookies",
                t.terms to "https://qrkontaq.com/terms",
                t.privacy to "https://qrkontaq.com/privacy",
            ).forEach { (label, url) ->
                DrawerItem(
                    iconSvg = com.qrkontaq.ui.admin.AdminIcons.ExternalLink,
                    label = label,
                    external = true,
                    onClick = { window.open(url, "_blank") },
                )
            }
        }

        Box(Modifier.fillMaxWidth().margin(top = 14.px), contentAlignment = Alignment.Center) {
            A(href = "https://qrkontaq.com", attrs = { classes("qk-plain") }) {
                Row(verticalAlignment = Alignment.CenterVertically) {
                    SpanText(
                        menuT(lang).madeWith,
                        Modifier.fontSize(12.px).color(Brand.TextSecondary),
                    )
                    SpanText(
                        "QRkontaq",
                        Modifier.fontSize(12.px).fontWeight(FontWeight.Bold).color(Brand.Emerald),
                    )
                }
            }
        }
    }
}

/**
 * Language picker next to the burger: hidden for one language, a glass
 * segmented toggle for two, a dropdown for three or more.
 */
@Composable
private fun LangControl(
    languages: List<String>,
    current: String,
    onChange: (String) -> Unit,
) {
    if (languages.size < 2) return

    if (languages.size == 2) {
        Row(
            Modifier
                .borderRadius(Brand.RadiusChip.px)
                .padding(3.px)
                .styleModifier {
                    property("background", "rgba(255,255,255,.92)")
                    property("backdrop-filter", "blur(10px)")
                    property("box-shadow", "0 2px 10px rgba(31,36,33,.18)")
                },
        ) {
            languages.forEach { code ->
                val selected = code == current
                Box(
                    Modifier
                        .backgroundColor(if (selected) Brand.Charcoal else Color.rgba(0, 0, 0, 0f))
                        .borderRadius(Brand.RadiusChip.px)
                        .padding(topBottom = 6.px, leftRight = 11.px)
                        .cursor(Cursor.Pointer)
                        .attrsModifier { classes("qk-press"); attr("role", "button"); attr("tabindex", "0") }
                        .onClick { onChange(code) },
                ) {
                    SpanText(
                        code.uppercase(),
                        Modifier
                            .fontSize(12.px)
                            .fontWeight(FontWeight.SemiBold)
                            .color(if (selected) Brand.Surface else Brand.Charcoal)
                            .attrsModifier { if (!selected) classes("qk-ink") },
                    )
                }
            }
        }
        return
    }

    // 3+ languages: dropdown.
    var open by remember { mutableStateOf(false) }
    Box(Modifier.position(Position.Relative)) {
        Row(
            Modifier
                .height(40.px)
                .borderRadius(Brand.RadiusChip.px)
                .padding(leftRight = 12.px)
                .cursor(Cursor.Pointer)
                .styleModifier {
                    property("background", "rgba(255,255,255,.92)")
                    property("backdrop-filter", "blur(10px)")
                    property("box-shadow", "0 2px 10px rgba(31,36,33,.18)")
                }
                .attrsModifier {
                    classes("qk-press")
                    attr("role", "button")
                    attr("aria-haspopup", "listbox")
                    attr("aria-expanded", "$open")
                }
                .onClick { open = !open },
            verticalAlignment = Alignment.CenterVertically,
        ) {
            SpanText(
                current.uppercase(),
                Modifier
                    .fontSize(12.px)
                    .fontWeight(FontWeight.SemiBold)
                    .color(Brand.Charcoal)
                    .margin(right = 5.px)
                    .attrsModifier { classes("qk-ink") },
            )
            InlineIcon(
                if (open) com.qrkontaq.ui.admin.AdminIcons.ChevronUp else com.qrkontaq.ui.admin.AdminIcons.ChevronDown,
                13,
                "#1F2421",
                extraClass = "qk-ink",
            )
        }
        if (open) {
            Column(
                Modifier
                    .position(Position.Absolute)
                    .top(46.px)
                    .right(0.px)
                    .minWidth(96.px)
                    .backgroundColor(Brand.Surface)
                    .borderRadius(14.px)
                    .padding(5.px)
                    .zIndex(20)
                    .boxShadow(offsetY = 8.px, blurRadius = 24.px, color = Color.rgba(31, 36, 33, 0.18f))
                    .attrsModifier { classes("qk-fade-in"); attr("role", "listbox") },
            ) {
                languages.forEach { code ->
                    val selected = code == current
                    Row(
                        Modifier
                            .fillMaxWidth()
                            .borderRadius(10.px)
                            .padding(topBottom = 8.px, leftRight = 11.px)
                            .cursor(Cursor.Pointer)
                            .then(if (selected) Modifier.backgroundColor(Brand.WarmBackground) else Modifier)
                            .attrsModifier { classes("qk-press"); attr("role", "option") }
                            .onClick {
                                onChange(code)
                                open = false
                            },
                        horizontalArrangement = Arrangement.SpaceBetween,
                        verticalAlignment = Alignment.CenterVertically,
                    ) {
                        SpanText(
                            code.uppercase(),
                            Modifier
                                .fontSize(13.px)
                                .fontWeight(if (selected) FontWeight.Bold else FontWeight.Medium)
                                .color(Brand.Charcoal),
                        )
                        if (selected) {
                            InlineIcon(com.qrkontaq.ui.admin.AdminIcons.Check, 13, "var(--qk-accent)")
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun QuickAction(
    iconSvg: String,
    label: String,
    onClick: () -> Unit,
) {
    Column(
        Modifier
            .cursor(Cursor.Pointer)
            .styleModifier { property("flex", "1"); property("max-width", "76px") }
            .attrsModifier { classes("qk-press"); attr("role", "button"); attr("aria-label", label) }
            .onClick { onClick() },
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        Box(
            Modifier
                .size(50.px)
                .borderRadius(Brand.RadiusChip.px)
                .attrsModifier { classes("qk-accent-soft") },
            contentAlignment = Alignment.Center,
        ) {
            InlineIcon(iconSvg, 19, "var(--qk-accent)")
        }
        SpanText(
            label,
            Modifier.fontSize(11.px).color(Brand.TextSecondary).margin(top = 5.px)
                .attrsModifier { classes("qk-nowrap") },
        )
    }
}

/** iOS-settings-style group: uppercase caption + card with rows. */
@Composable
private fun DrawerGroup(
    title: String,
    content: @Composable () -> Unit,
) {
    SpanText(
        title,
        Modifier
            .fontSize(11.px)
            .fontWeight(FontWeight.Bold)
            .color(Brand.TextSecondary)
            .margin(bottom = 6.px, left = 4.px)
            .styleModifier {
                property("letter-spacing", ".08em")
                property("text-transform", "uppercase")
            },
    )
    Column(
        Modifier
            .fillMaxWidth()
            .backgroundColor(Brand.Surface)
            .borderRadius(14.px)
            .padding(4.px)
            .margin(bottom = 14.px)
            .boxShadow(offsetY = 1.px, blurRadius = 4.px, color = Color.rgba(31, 36, 33, 0.05f)),
    ) {
        content()
    }
}

@Composable
private fun DrawerItem(
    iconSvg: String,
    label: String,
    sub: String? = null,
    accented: Boolean = false,
    external: Boolean = false,
    expandable: Boolean = false,
    expanded: Boolean = false,
    onClick: (() -> Unit)? = null,
) {
    Row(
        Modifier
            .fillMaxWidth()
            .borderRadius(11.px)
            .padding(topBottom = 9.px, leftRight = 9.px)
            .then(if (onClick != null) Modifier.cursor(Cursor.Pointer) else Modifier)
            .attrsModifier {
                classes("qk-press")
                if (onClick != null) attr("role", "button")
                if (expandable) attr("aria-expanded", "$expanded")
            }
            .onClick { onClick?.invoke() },
        horizontalArrangement = Arrangement.SpaceBetween,
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Row(Modifier.fillMaxWidth().margin(right = 8.px), verticalAlignment = Alignment.CenterVertically) {
            Box(
                Modifier
                    .size(32.px)
                    .minWidth(32.px)
                    .borderRadius(10.px)
                    .backgroundColor(Brand.WarmBackground)
                    .margin(right = 10.px),
                contentAlignment = Alignment.Center,
            ) {
                InlineIcon(iconSvg, 15, if (accented) "var(--qk-accent)" else "#6B7280")
            }
            Column {
                SpanText(
                    label,
                    Modifier
                        .fontSize(13.px)
                        .fontWeight(FontWeight.Medium)
                        .then(
                            if (accented) {
                                Modifier.styleModifier { property("color", "var(--qk-accent)") }
                            } else {
                                Modifier.color(Brand.Charcoal)
                            },
                        ),
                )
                sub?.let {
                    SpanText(it, Modifier.fontSize(11.px).color(Brand.TextSecondary).margin(top = 1.px))
                }
            }
        }
        when {
            expandable ->
                InlineIcon(
                    if (expanded) com.qrkontaq.ui.admin.AdminIcons.ChevronUp else com.qrkontaq.ui.admin.AdminIcons.ChevronDown,
                    13,
                    "#9CA3AF",
                )
            external -> InlineIcon(com.qrkontaq.ui.admin.AdminIcons.ExternalLink, 12, "#9CA3AF")
        }
    }
}

/** Expanded content under an expandable DrawerItem. */
@Composable
private fun DrawerDetail(content: @Composable () -> Unit) {
    Column(
        Modifier
            .fillMaxWidth()
            .backgroundColor(Brand.WarmBackground)
            .borderRadius(11.px)
            .padding(12.px)
            .margin(bottom = 4.px, left = 4.px, right = 4.px)
            .attrsModifier { classes("qk-fade-in") },
    ) {
        content()
    }
}

@Composable
private fun CategoryChips(
    categories: List<MenuCategoryDto>,
    thumbnails: Map<String, String?>,
    activeSlug: String?,
    lang: String,
    accent: Color,
) {
    Row(
        Modifier
            .fillMaxWidth()
            .position(Position.Sticky)
            .top(0.px)
            .zIndex(10)
            .padding(top = 10.px, bottom = 10.px)
            .attrsModifier { classes("qk-sticky") },
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Row(
        Modifier
            .fillMaxWidth()
            .minWidth(0.px)
            .overflow { x(Overflow.Auto) }
            // Inset inside the scroller: chips clip flush at the viewport edge
            // and the last chip still gets breathing room at scroll end.
            .padding(leftRight = 8.px)
            .styleModifier { property("white-space", "nowrap") }
            .attrsModifier { classes("qk-hide-scrollbar") },
    ) {
        categories.forEach { category ->
            val active = category.slug == activeSlug
            Row(
                Modifier
                    .id("chip-${category.slug}")
                    .margin(leftRight = 4.px)
                    .height(38.px)
                    .backgroundColor(if (active) accent else Brand.Surface)
                    .border(1.px, LineStyle.Solid, if (active) accent else Brand.Divider)
                    .borderRadius(Brand.RadiusChip.px)
                    .padding(left = if (thumbnails[category.slug] != null) 5.px else 14.px, right = 15.px)
                    .cursor(Cursor.Pointer)
                    .attrsModifier {
                        classes("qk-press")
                        attr("role", "button")
                        attr("tabindex", "0")
                    }
                    .onClick {
                        document.getElementById("cat-${category.slug}")
                            ?.scrollIntoView(js("{ behavior: 'smooth', block: 'start' }"))
                    },
                verticalAlignment = Alignment.CenterVertically,
            ) {
                val thumb = thumbnails[category.slug]
                if (thumb != null) {
                    Image(
                        src = thumb,
                        modifier = Modifier
                            .size(26.px)
                            .minWidth(26.px)
                            .borderRadius(Brand.RadiusChip.px)
                            .objectFit(ObjectFit.Cover)
                            .margin(right = 8.px)
                            .border(
                                1.px,
                                LineStyle.Solid,
                                if (active) Color.rgba(255, 255, 255, 0.6f) else Brand.Divider,
                            )
                            .attrsModifier { attr("alt", "") },
                    )
                }
                SpanText(
                    category.name.localized(lang),
                    Modifier
                        .fontSize(14.px)
                        .fontWeight(if (active) FontWeight.SemiBold else FontWeight.Medium)
                        .color(if (active) Brand.Surface else Brand.Charcoal),
                )
            }
        }
        }

    }
}

@Composable
private fun EmptySearchView(lang: String, onReset: () -> Unit) {
    val t = menuT(lang)
    Column(
        Modifier.fillMaxWidth().padding(topBottom = 48.px, leftRight = 16.px),
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        Box(
            Modifier
                .size(72.px)
                .borderRadius(Brand.RadiusChip.px)
                .backgroundColor(Brand.Surface)
                .boxShadow(offsetY = 2.px, blurRadius = 10.px, color = Color.rgba(31, 36, 33, 0.06f)),
            contentAlignment = Alignment.Center,
        ) {
            InlineIcon(MenuIcons.Search, 28, "#C9C3BA")
        }
        SpanText(
            t.nothingFound,
            Modifier
                .fontSize(17.px)
                .fontWeight(FontWeight.Bold)
                .color(Brand.Charcoal)
                .margin(top = 16.px),
        )
        SpanText(
            t.tryDifferentFilters,
            Modifier.fontSize(13.px).color(Brand.TextSecondary).margin(top = 4.px),
        )
        Box(
            Modifier
                .margin(top = 16.px)
                .borderRadius(Brand.RadiusChip.px)
                .padding(topBottom = 9.px, leftRight = 18.px)
                .cursor(Cursor.Pointer)
                .attrsModifier {
                    classes("qk-press", "qk-accent-soft")
                    attr("role", "button")
                }
                .onClick { onReset() },
        ) {
            SpanText(
                t.resetFilters,
                Modifier
                    .fontSize(13.px)
                    .fontWeight(FontWeight.SemiBold)
                    .styleModifier { property("color", "var(--qk-accent)") },
            )
        }
    }
}

@Composable
private fun MenuLoadingView() {
    Box(
        Modifier.fillMaxWidth().minHeight(100.vh)
            .attrsModifier { classes("qk-paper") },
        contentAlignment = Alignment.TopCenter,
    ) {
        Column(Modifier.fillMaxWidth().maxWidth(640.px)) {
            InjectMenuStyles(Brand.DefaultAccentHex)
            Box(Modifier.fillMaxWidth().height(200.px).attrsModifier { classes("qk-skeleton") })
            Column(Modifier.fillMaxWidth().padding(16.px)) {
                Row(Modifier.fillMaxWidth().margin(top = (-64).px).zIndex(2)) {
                    Box(
                        Modifier
                            .size(60.px)
                            .borderRadius(16.px)
                            .margin(right = 12.px)
                            .attrsModifier { classes("qk-skeleton") },
                    )
                    Column {
                        Box(
                            Modifier.width(180.px).height(22.px).borderRadius(8.px)
                                .attrsModifier { classes("qk-skeleton") },
                        )
                        Box(
                            Modifier.width(120.px).height(14.px).borderRadius(7.px).margin(top = 8.px)
                                .attrsModifier { classes("qk-skeleton") },
                        )
                    }
                }
                Row(Modifier.fillMaxWidth().margin(top = 24.px).gap(8.px)) {
                    repeat(4) {
                        Box(
                            Modifier.width(88.px).height(36.px).borderRadius(Brand.RadiusChip.px)
                                .attrsModifier { classes("qk-skeleton") },
                        )
                    }
                }
                repeat(5) {
                    Row(
                        Modifier
                            .fillMaxWidth()
                            .margin(top = 12.px)
                            .backgroundColor(Brand.Surface)
                            .borderRadius(Brand.RadiusCard.px)
                            .padding(12.px),
                    ) {
                        Column(Modifier.fillMaxWidth()) {
                            Box(
                                Modifier.width(150.px).height(16.px).borderRadius(8.px)
                                    .attrsModifier { classes("qk-skeleton") },
                            )
                            Box(
                                Modifier.fillMaxWidth(85.percent).height(12.px).borderRadius(6.px)
                                    .margin(top = 10.px).attrsModifier { classes("qk-skeleton") },
                            )
                            Box(
                                Modifier.width(60.px).height(16.px).borderRadius(8.px).margin(top = 14.px)
                                    .attrsModifier { classes("qk-skeleton") },
                            )
                        }
                        Box(
                            Modifier.size(92.px).minWidth(92.px).borderRadius(14.px).margin(left = 12.px)
                                .attrsModifier { classes("qk-skeleton") },
                        )
                    }
                }
            }
        }
    }
}

@Composable
private fun MenuNotFoundView() {
    // No venue config here — fall back to the visitor's system language.
    val t = menuT(com.qrkontaq.utils.systemLanguageCode())
    Box(
        Modifier.fillMaxWidth().minHeight(100.vh).backgroundColor(Brand.WarmBackground),
        contentAlignment = Alignment.Center,
    ) {
        Column(horizontalAlignment = Alignment.CenterHorizontally) {
            InjectMenuStyles(Brand.DefaultAccentHex)
            Box(
                Modifier
                    .size(72.px)
                    .borderRadius(Brand.RadiusChip.px)
                    .backgroundColor(Brand.Surface)
                    .boxShadow(offsetY = 2.px, blurRadius = 10.px, color = Color.rgba(31, 36, 33, 0.06f)),
                contentAlignment = Alignment.Center,
            ) {
                InlineIcon(MenuIcons.Utensils, 28, "#C9C3BA")
            }
            SpanText(
                t.venueNotFound,
                Modifier
                    .fontSize(20.px)
                    .fontWeight(FontWeight.Bold)
                    .color(Brand.Charcoal)
                    .margin(top = 16.px)
                    .attrsModifier { classes("qk-display") },
            )
            A(href = "https://qrkontaq.com", attrs = { classes("qk-plain") }) {
                SpanText(
                    "qrkontaq.com",
                    Modifier.fontSize(14.px).color(Brand.Emerald).margin(top = 8.px),
                )
            }
        }
    }
}

@Composable
fun FrozenView(restaurant: PublicRestaurantDto?) {
    val t =
        menuT(
            com.qrkontaq.utils.resolveLanguage(
                saved = null,
                supported = restaurant?.languages ?: emptyList(),
                fallback = restaurant?.defaultLanguage ?: "en",
            ),
        )
    Box(
        Modifier.fillMaxWidth().minHeight(100.vh).backgroundColor(Brand.WarmBackground),
        contentAlignment = Alignment.Center,
    ) {
        Column(
            Modifier.padding(24.px),
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            InjectMenuStyles(Brand.accentHexFrom(restaurant?.accentColor))
            restaurant?.logoUrl?.let { logo ->
                Image(
                    src = logo,
                    modifier = Modifier
                        .size(72.px)
                        .borderRadius(18.px)
                        .objectFit(ObjectFit.Cover)
                        .margin(bottom = 16.px),
                )
            } ?: Box(
                Modifier
                    .size(72.px)
                    .borderRadius(Brand.RadiusChip.px)
                    .backgroundColor(Brand.Surface)
                    .margin(bottom = 16.px)
                    .boxShadow(offsetY = 2.px, blurRadius = 10.px, color = Color.rgba(31, 36, 33, 0.06f)),
                contentAlignment = Alignment.Center,
            ) {
                InlineIcon(MenuIcons.Clock, 28, "#C9C3BA")
            }
            SpanText(
                restaurant?.name ?: "",
                Modifier
                    .fontSize(22.px)
                    .fontWeight(FontWeight.Bold)
                    .color(Brand.Charcoal)
                    .textAlign(TextAlign.Center)
                    .attrsModifier { classes("qk-display") },
            )
            SpanText(
                t.menuUnavailable,
                Modifier
                    .fontSize(15.px)
                    .color(Brand.TextSecondary)
                    .margin(top = 8.px)
                    .textAlign(TextAlign.Center),
            )
            SpanText(
                t.madeWith + "QRkontaq",
                Modifier.fontSize(12.px).color(Brand.TextSecondary).margin(top = 32.px),
            )
        }
    }
}

@Composable
private fun PoweredByFooter(lang: String) {
    Column(
        Modifier.fillMaxWidth().padding(top = 32.px, bottom = 28.px),
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        Box(Modifier.width(48.px).height(1.px).backgroundColor(Brand.Divider).margin(bottom = 16.px))
        A(href = "https://qrkontaq.com", attrs = { classes("qk-plain") }) {
            Row(verticalAlignment = Alignment.CenterVertically) {
                SpanText(
                    menuT(lang).madeWith,
                    Modifier.fontSize(12.px).color(Brand.TextSecondary),
                )
                SpanText(
                    "QRkontaq",
                    Modifier.fontSize(12.px).fontWeight(FontWeight.Bold).color(Brand.Emerald),
                )
            }
        }
    }
}
